<template>
    <div class="chat-composer">
        <div v-if="attachments.length > 0" class="chat-composer__attachments">
            <div v-for="(item, index) in attachments" :key="item.url" class="chat-composer__attachment">
                <img :src="item.url" alt="">
                <button type="button" class="chat-composer__remove" :title="$t('Remove')" @click="removeAttachment(index)">×</button>
            </div>
        </div>
        <div class="chat-composer__row">
            <button type="button" class="chat-composer__icon" :title="$t('AttachPhotos')" @click="$refs.fileInput.click()">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
            </button>
            <input ref="fileInput" type="file" accept="image/*" multiple class="chat-composer__file" @change="onFileChange">
            <textarea
                ref="input"
                v-model="text"
                rows="1"
                class="chat-composer__input"
                :placeholder="$t('WriteMessage')"
                @input="autoGrow"
                @keydown.enter="onEnter"
                @paste="onPaste"></textarea>
            <button type="button" class="chat-composer__send" :disabled="!canSend" :title="$t('Send')" @click="submit">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
            </button>
        </div>
    </div>
</template>

<script>
// Pole do pisania: rośnie z treścią do kilku linii, Enter wysyła, Shift+Enter łamie linię.
// Zdjęcia z przycisku, ze schowka albo przeciągnięte na panel (addFiles z rodzica).
const MAX_HEIGHT = 140

export default {
    data() {
        return {
            text: '',
            attachments: []
        }
    },
    computed: {
        canSend() {
            // Treść wymagana jak dotąd — pusta wiadomość ze zdjęciem trafiłaby do bazy jako null.
            return this.text.trim() !== ''
        }
    },
    beforeDestroy() {
        this.attachments.forEach(a => URL.revokeObjectURL(a.url))
    },
    methods: {
        onEnter(event) {
            // isComposing: Enter zatwierdzający znak w IME nie wysyła wiadomości.
            if (event.shiftKey || event.isComposing) return
            event.preventDefault()
            this.submit()
        },
        submit() {
            if (!this.canSend) return

            this.$emit('send', {
                content: this.text.trim(),
                files: this.attachments.map(a => a.file)
            })

            this.attachments.forEach(a => URL.revokeObjectURL(a.url))
            this.attachments = []
            this.text = ''
            this.$nextTick(this.autoGrow)
        },
        addFiles(files) {
            Array.from(files)
                .filter(f => f.type != null && f.type.startsWith('image/'))
                .forEach(file => this.attachments.push({ file, url: URL.createObjectURL(file) }))
        },
        onFileChange(event) {
            this.addFiles(event.target.files)
            // Ten sam plik wybrany drugi raz też ma wywołać change.
            event.target.value = ''
        },
        onPaste(event) {
            const files = Array.from((event.clipboardData && event.clipboardData.files) || [])
            if (files.some(f => f.type.startsWith('image/'))) {
                event.preventDefault()
                this.addFiles(files)
            }
        },
        removeAttachment(index) {
            URL.revokeObjectURL(this.attachments[index].url)
            this.attachments.splice(index, 1)
        },
        autoGrow() {
            const input = this.$refs.input
            if (!input) return
            input.style.height = 'auto'
            input.style.height = Math.min(input.scrollHeight, MAX_HEIGHT) + 'px'
        },
        focus() {
            if (this.$refs.input) this.$refs.input.focus()
        }
    }
}
</script>

<style scoped>
.chat-composer {
    border-top: 1px solid var(--messages-panel-border);
    padding: 8px 10px;
    background: var(--chat-composer-bg);
}

.chat-composer__row {
    display: flex;
    align-items: flex-end;
    gap: 6px;
}

.chat-composer__input {
    flex: 1;
    resize: none;
    min-height: 38px;
    max-height: 140px;
    padding: 8px 12px;
    border: 1px solid var(--input-border);
    border-radius: 18px;
    background: var(--input-bg);
    color: var(--text);
    font-size: 14px;
    line-height: 20px;
    outline: none;
}

.chat-composer__input:focus {
    border-color: var(--accent);
}

.chat-composer__file {
    display: none;
}

.chat-composer__icon,
.chat-composer__send {
    flex: 0 0 38px;
    height: 38px;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.chat-composer__icon {
    background: transparent;
    color: var(--text-muted);
}

.chat-composer__icon:hover {
    background: var(--surface-hover);
    color: var(--text);
}

.chat-composer__send {
    background: var(--chat-bubble-mine-bg);
    color: var(--chat-bubble-mine-fg);
}

.chat-composer__send:disabled {
    opacity: 0.4;
    cursor: default;
}

.chat-composer__attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
}

.chat-composer__attachment {
    position: relative;
    width: 64px;
    height: 64px;
}

.chat-composer__attachment img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 6px;
}

.chat-composer__remove {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 20px;
    height: 20px;
    border: none;
    border-radius: 50%;
    background: var(--danger);
    color: #fff;
    line-height: 18px;
    font-size: 14px;
    padding: 0;
    cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/Systems/Tickets/ChatComposer.vue